import Flex from 'antd/lib/flex'
import Form from 'antd/lib/form'
import { FormInstance } from 'antd/lib/form/Form'
import { useEffect, useMemo } from 'react'
import classes from '../GenerateResponseForm/GenerateResponseForm.module.less'
import styles from './ManualResponseForm.module.less'
import { ManualResponseFormType, PromptAnnotationsType } from 'Types/Tasks.types'
import Select from 'antd/lib/select'
import { LMFInAccurateReasonOptions } from 'Constants/customTask.constants'
import { ModelFeedbackFormField } from '../ModelFeedback/ModelFeedbackFormField'
import { CustomForm } from 'Types/CustomTask.types'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import FieldWithMarkdownPreview from 'Components/common/FieldWithMarkdownPreview/FieldWithMarkdownPreview'
import TextArea from 'antd/lib/input/TextArea'
import { LiveFeedback } from 'Tasks/CustomTask/UI/common/LiveFeedback/LiveFeedback'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { LiveFeedbackPlugin } from 'Plugins/LiveFeedback/LiveFeedback.plugin'
import { LiveFeedbackContextType, LiveFeedbackPluginConfigType } from 'Types/LiveFeedback.types'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'

type GenerateResponseFormPropsType = {
    form: FormInstance<ManualResponseFormType>
    onSubmit: () => void
    initialValues?: ManualResponseFormType
    isReviewMode?: boolean
    isReadOnly?: boolean
    modelResponseExtraInfo: {
        totalModelResponsesOnPromptRevision: number
        modelResponseFeedbackCode: { value: number; label: string }[]
        modelResponse: string
    }
    setForms: (forms: CustomForm) => void
    attributesUI?: UpdatableReactElement
    featureFlags: ProjectFeatureFlags['feature_flags']
    promptAnnotations?: PromptAnnotationsType
    installedPlugins?: IPlugin[]
    question: string
    setIsLiveFeedbackChecked: () => void
    isLMFProject?: boolean
    isStemProject?: boolean
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
}

export const ManualResponseForm = (props: GenerateResponseFormPropsType) => {
    const {
        form,
        isReadOnly,
        setForms,
        featureFlags,
        promptAnnotations,
        initialValues,
        modelResponseExtraInfo,
        attributesUI,
        installedPlugins,
        question,
        setIsLiveFeedbackChecked,
        isLMFProject,
        isStemProject,
        liveFeedbackContext,
        areAttributesValid,
    } = props
    const feedbackValues = Form.useWatch('modelFeedbackDetails', form)
    const humanCompleteFinalAnswer = Form.useWatch('humanCompleteFinalAnswer', form)
    const humanReasoningAnswer = Form.useWatch('humanFinalAnswerReasoning', form)

    useEffect(() => {
        setForms({ manualResponseForm: form, finalSubmissionForm: form })
    }, [form, setForms])

    useEffect(() => {
        if (initialValues) {
            return
        }

        if (promptAnnotations) {
            const { accuracy, human_complete_final_answer, human_final_answer_reasoning } =
                promptAnnotations
            form.setFieldsValue({
                accuracy: accuracy
                    ? LMFInAccurateReasonOptions.find(option => option.value === accuracy)?.value
                    : LMFInAccurateReasonOptions[0].value,
                humanCompleteFinalAnswer: human_complete_final_answer,
                humanFinalAnswerReasoning: human_final_answer_reasoning,
            })
        }

        if (modelResponseExtraInfo.modelResponseFeedbackCode) {
            form.setFieldsValue({
                modelFeedbackDetails: modelResponseExtraInfo.modelResponseFeedbackCode.map(
                    feedbackCode => ({
                        feedbackResultCode: feedbackCode.value,
                    })
                ),
            })
        }
    }, [form, initialValues, modelResponseExtraInfo.modelResponseFeedbackCode, promptAnnotations])

    const domain = useMemo(() => {
        if (isLMFProject) return 'LMF'
        if (isStemProject) return 'Stem'
        return 'Math'
    }, [isLMFProject, isStemProject])

    const liveFeedbackPlugin = useMemo(() => {
        return installedPlugins?.find(plugin => plugin.id === 'Live Feedback') as LiveFeedbackPlugin
    }, [installedPlugins])

    const additionalFields = useMemo(() => {
        if (attributesUI && !featureFlags?.ots_project) {
            return (
                <ModelFeedbackFormField
                    feedbackValues={feedbackValues}
                    modelResponseFeedbackCode={modelResponseExtraInfo.modelResponseFeedbackCode}
                />
            )
        }
        return (
            <>
                {!featureFlags?.ots_project && (
                    <Form.Item<ManualResponseFormType>
                        className={styles.inputField}
                        rules={[{ required: true, message: 'Please select the option' }]}
                        name="accuracy"
                        label={
                            <p className={styles.inputTitle}>
                                Was the model response and final answer correct?
                            </p>
                        }
                    >
                        <Select className={styles.input} options={LMFInAccurateReasonOptions} />
                    </Form.Item>
                )}
                <Flex style={{ width: '100%' }} vertical>
                    <div className={`${featureFlags?.ots_project ? classes.halfWidth : ''}`}>
                        <FieldWithMarkdownPreview
                            component={
                                <Form.Item<ManualResponseFormType>
                                    className={styles.inputField}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'Please enter the correct final human answer',
                                        },
                                    ]}
                                    name="humanCompleteFinalAnswer"
                                    label={
                                        <p className={styles.inputTitle}>
                                            Correct Final Human Answer
                                        </p>
                                    }
                                >
                                    <TextArea rows={featureFlags?.ots_project ? 3 : 10} />
                                </Form.Item>
                            }
                            value={humanCompleteFinalAnswer}
                            showPreview={!!featureFlags?.ots_project}
                        />
                    </div>

                    <FieldWithMarkdownPreview
                        component={
                            <Form.Item<ManualResponseFormType>
                                className={styles.inputField}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter the explanation',
                                    },
                                ]}
                                name="humanFinalAnswerReasoning"
                                label={
                                    <p className={styles.inputTitle}>
                                        {!featureFlags?.ots_project
                                            ? 'Briefly explain how you got to your final human answer'
                                            : `Please provide the complete Chain-of-Thought explanation,
                                        which needs to be detailed, providing the step-by-step
                                        logical process, reasoning, and calculations needed to
                                        arrive at the correct answer from the information given in
                                        the prompt.`}
                                    </p>
                                }
                            >
                                <TextArea rows={10} />
                            </Form.Item>
                        }
                        value={humanReasoningAnswer}
                        showPreview={!!featureFlags?.ots_project}
                    />

                    <ModelFeedbackFormField
                        feedbackValues={feedbackValues}
                        modelResponseFeedbackCode={modelResponseExtraInfo.modelResponseFeedbackCode}
                    />
                </Flex>
            </>
        )
    }, [
        feedbackValues,
        attributesUI,
        modelResponseExtraInfo.modelResponseFeedbackCode,
        featureFlags,
        humanCompleteFinalAnswer,
        humanReasoningAnswer,
    ])

    return (
        <div
            className={`${classes.basicInfo}} ${
                featureFlags?.ots_project || featureFlags.lmf_project ? classes.padding : ''
            }`}
        >
            {!featureFlags?.ots_project && (
                <div className={classes['width-50']}>{attributesUI?.element ?? null}</div>
            )}
            <Form
                form={form}
                layout="vertical"
                initialValues={initialValues}
                className={classes.inputContainer}
                requiredMark={false}
                disabled={isReadOnly}
            >
                {additionalFields}
            </Form>

            {featureFlags?.ots_project && (
                <div className={styles.ots_attribute_container}>
                    {attributesUI?.element ?? null}
                </div>
            )}

            <LiveFeedback
                isEnabled={!!liveFeedbackPlugin}
                modelResponse={modelResponseExtraInfo.modelResponse}
                humanAnswer={humanCompleteFinalAnswer}
                humanReasoningAnswer={humanReasoningAnswer}
                domain={domain}
                pluginId={liveFeedbackPlugin?.getConfig().id ?? ''}
                pluginConfig={
                    (liveFeedbackPlugin?.getConfig() as PluginConfigType<LiveFeedbackPluginConfigType>) ??
                    null
                }
                question={question}
                setIsLiveFeedbackChecked={setIsLiveFeedbackChecked}
                liveFeedbackContext={liveFeedbackContext}
                areAttributesValid={areAttributesValid}
            />
        </div>
    )
}
